import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getLenderControlsThunk, getLendersListThunk, getLenderThunk } from 'actions/lenderAction';
import {
  getLenderVehiclesByStockThunk,
  fetchDealSourceDataThunk,
  getVehiclesForLenderThunk,
  submitDealSourceDataThunk,
} from 'actions/vehicleActions';
import { IIncomeForm } from 'components/IncomeCalculator/types';
import { IAvailableFilters, ILenderVehicle, IVehicleFiltersDirtyFields } from 'modules/core/types';
import { INCOME_INITIAL_VALUES, INITIAL_VALUES_ONE_LENDER, LTV_INC_TAXES_FEES } from 'modules/Lender/Lender.const';
import {
  ILenderControlsResponse,
  ILenderForm,
  ILenderRequest,
  ILendersListItem,
} from 'modules/Lender/types/Lender.types';
import { IDealSourceData, IDealSourceDataFlags } from 'modules/core/types/dealSource';
import {
  DEAL_SOURCE_DATA_FLAGS_INITIAL,
  computeGetFulfilledFlags,
  computeGetPendingFlags,
  computeGetRejectedFlags,
  computePostFulfilledFlags,
  computePostPendingFlags,
  computePostRejectedFlags,
} from 'store/utils/dealSourceDataFlags.utils';
import { preserveManuallyEnteredVehicleInfo } from 'store/utils/dealSourceData.utils';

import { getHashTableIdByLenderName } from './utils/lendersHashTable.utils';

export interface ILenderState extends ILenderRequest {
  name: string;
  path: string;
  initialValues: ILenderForm;
  initialErrors: Record<string, string>;
  vehicles: {
    vehiclesByParams?: ILenderVehicle[];
    vehiclesByStock?: ILenderVehicle[];
    sortedVehicles?: ILenderVehicle[];
  };
  income: IIncomeForm;
  isTableView: boolean;
  vehicleFiltersDirtyFields: IVehicleFiltersDirtyFields;
  firstDealSourceRender: boolean;
  resetCount: number;
  isFetchingVehicles: boolean;
  isFetchingVehiclesCount: boolean;
  isFetchingVehiclesFailed: boolean;
  fetchVehiclesError?: any;
  notFoundVehicles: string[];
  notAvailableVehicles: string[];
  availableFilters?: IAvailableFilters;
  dealSourceDataFlags: IDealSourceDataFlags;
  dealSourceData: IDealSourceData;
  route_one_disable_submit?: boolean;
  dealSourceTierEditable: boolean;
  balloonInitDataLoaded: boolean;
  isShopByBudgetEnabled: boolean;
  isShopByCallbackEnabled: boolean;
  isSalespersonAccess: boolean;
  isBalloonSalespersonAccess: boolean;
  withoutLenderRedirect: boolean;
  uiName?: string | undefined;
}

export interface ITotalVehicles {
  totalVehiclesByParams?: number;
  totalVehiclesByStock?: number;
}

export interface ILendersState {
  lendersHashTable: Record<string, ILenderState>;
  isFetchingLendersList: boolean;
  isFetchingLender: boolean;
  isLoadedLender: boolean;
  currentLenderId: string;
  currentLenderControls: {
    maxVSC: number;
    maxGap: number;
    showRates: boolean;
    showAdvancedOverrides: boolean;
    isVscTaxable: boolean;
    isGapTaxable: boolean;
  };
}

const initialState: ILendersState = {
  lendersHashTable: {},
  isFetchingLendersList: true,
  isFetchingLender: false,
  isLoadedLender: false,
  currentLenderId: '',
  currentLenderControls: {
    maxVSC: 0,
    maxGap: 0,
    showRates: false,
    showAdvancedOverrides: false,
    isVscTaxable: true,
    isGapTaxable: true,
  },
};

export const initialStateOneLender: ILenderState = {
  name: '',
  path: '',
  initialValues: INITIAL_VALUES_ONE_LENDER,
  initialErrors: {},
  id: 0,
  vehicles: {
    vehiclesByParams: undefined,
    vehiclesByStock: undefined,
    sortedVehicles: undefined,
  },
  income: { totalIncome: 0, savedIncomes: INCOME_INITIAL_VALUES },
  resetCount: 0,
  isTableView: true,
  guidebooks: [],
  logo: '',
  main_page_logo: null,
  main_page_logo_link: null,
  main_page_logo_display_upgrade_inventory: false,
  adjust_deal_logo: null,
  adjust_deal_logo_link: null,
  adjust_deal_logo_display_upgrade_inventory: false,
  ltv_calculation_method: '',
  status: '',
  tiers: [],
  route_one_app: false,
  route_one_id: null,
  vehicleFiltersDirtyFields: {},
  firstDealSourceRender: true,
  isFetchingVehicles: false,
  isFetchingVehiclesCount: false,
  isFetchingVehiclesFailed: false,
  notFoundVehicles: [],
  notAvailableVehicles: [],
  availableFilters: undefined,
  dealSourceDataFlags: DEAL_SOURCE_DATA_FLAGS_INITIAL,
  dealSourceData: {} as IDealSourceData,
  dealSourceTierEditable: false,
  afg: false,
  afg_id: null,
  balloonInitDataLoaded: false,
  isShopByBudgetEnabled: false,
  isShopByCallbackEnabled: false,
  isSalespersonAccess: false,
  isBalloonSalespersonAccess: false,
  withoutLenderRedirect: false,
  uiName: undefined,
  lender_list: false,
};

export const lenderSlice = createSlice({
  name: 'lenders',
  initialState,
  reducers: {
    syncInitialValues: (state: ILendersState, { payload: initialValues }: PayloadAction<ILenderForm>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: { ...state.lendersHashTable[state.currentLenderId], initialValues },
      },
    }),
    syncInitialErrors: (state: ILendersState, { payload: initialErrors }: PayloadAction<Record<string, string>>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: { ...state.lendersHashTable[state.currentLenderId], initialErrors },
      },
    }),
    setCurrentLenderId: (state: ILendersState, { payload }: PayloadAction<string>) => ({
      ...state,
      currentLenderId: payload,
    }),
    setBalloonInitDataLoaded: (state: ILendersState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          balloonInitDataLoaded: payload,
        },
      },
    }),
    resetVehicles: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          vehicles: {
            vehiclesByParams: undefined,
            vehiclesByStock: undefined,
            sortedVehicles: undefined,
          },
          availableFilters: undefined,
          totalVehicles: {
            totalVehiclesByParams: undefined,
            totalVehiclesByStock: undefined,
          },
        },
      },
    }),
    resetVehiclesByStock: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          vehicles: {
            ...state.lendersHashTable[state.currentLenderId].vehicles,
            vehiclesByStock: undefined,
            sortedVehicles: undefined,
          },
          availableFilters:
            state.lendersHashTable[state.currentLenderId].vehicles.vehiclesByParams !== undefined
              ? state.lendersHashTable[state.currentLenderId].availableFilters
              : undefined,
        },
      },
    }),
    resetVehiclesByParams: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          vehicles: {
            ...state.lendersHashTable[state.currentLenderId].vehicles,
            vehiclesByParams: undefined,
            sortedVehicles: undefined,
          },
          availableFilters: state.lendersHashTable[state.currentLenderId].availableFilters,
        },
      },
    }),
    resetByStockOptions: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          notFoundVehicles: [],
          notAvailableVehicles: [],
        },
      },
    }),
    resetByStock: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          vehicles: {
            ...state.lendersHashTable[state.currentLenderId].vehicles,
            vehiclesByStock: undefined,
            sortedVehicles: undefined,
          },
          availableFilters:
            state.lendersHashTable[state.currentLenderId].vehicles.vehiclesByParams !== undefined
              ? state.lendersHashTable[state.currentLenderId].availableFilters
              : undefined,
          notFoundVehicles: [],
          notAvailableVehicles: [],
        },
      },
    }),
    setIncome: (state: ILendersState, { payload: income }: PayloadAction<IIncomeForm>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          income,
        },
      },
    }),
    setFirstDealSourceRenderOnLender: (state: ILendersState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          firstDealSourceRender: payload,
        },
      },
    }),
    resetIncome: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          income: initialStateOneLender.income,
        },
      },
    }),
    incrementResetCount: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          resetCount: (state.lendersHashTable[state.currentLenderId]?.resetCount ?? 0) + 1,
        },
      },
    }),
    resetResetCount: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          resetCount: 0,
        },
      },
    }),
    resetVehicleFiltersDirtyFields: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          vehicleFiltersDirtyFields: {},
        },
      },
    }),
    setDealSourceData: (state: ILendersState, { payload }: PayloadAction<any>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          dealSourceData: payload,
          dealSourceDataFlags: {
            ...state.lendersHashTable[state.currentLenderId].dealSourceDataFlags,
            isUpdated: true,
          },
        },
      },
    }),
    resetDealSourceData: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          dealSourceData: initialStateOneLender.dealSourceData,
        },
      },
    }),
    resetDealSourceDataFlags: (state: ILendersState) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          dealSourceDataFlags: initialStateOneLender.dealSourceDataFlags,
        },
      },
    }),
    setIsTableView: (state: ILendersState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          isTableView: payload,
        },
      },
    }),
    setDealSourceTierEditable: (state: ILendersState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: { ...state.lendersHashTable[state.currentLenderId], dealSourceTierEditable: payload },
      },
    }),
    setSortedVehiclesData: (state: ILendersState, { payload }: PayloadAction<ILenderVehicle[]>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          vehicles: {
            ...state.lendersHashTable[state.currentLenderId].vehicles,
            sortedVehicles: payload,
          },
        },
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(
      getLendersListThunk.fulfilled,
      (state: ILendersState, { payload }: PayloadAction<ILendersListItem[]>) => ({
        ...state,
        lendersHashTable: payload.reduce((lenderListObj, lender) => {
          const fullId: string = lender.path.split('/')[2];
          const id = Number(fullId.slice(0, fullId.indexOf('-')));
          const isShopByBudgetEnabled = !!lender.shop_by_budget_enabled;
          const isShopByCallbackEnabled = !!lender.shop_by_callback_enabled;
          const isSalespersonAccess = !!lender.salesperson_access;
          const isBalloonSalespersonAccess = !!lender.balloon_salesperson_access;
          const withoutLenderRedirect = !!lender.without_lender_redirect;
          const uiName = lender.ui_name;

          delete lender.shop_by_budget_enabled;
          delete lender.shop_by_callback_enabled;
          delete lender.salesperson_access;
          delete lender.balloon_salesperson_access;
          delete lender.without_lender_redirect;
          delete lender.ui_name;

          return {
            ...lenderListObj,
            [fullId]: {
              ...initialStateOneLender,
              ...lender,
              id,
              isShopByBudgetEnabled,
              isShopByCallbackEnabled,
              isSalespersonAccess,
              isBalloonSalespersonAccess,
              withoutLenderRedirect,
              uiName,
            },
          };
        }, {} as Record<string, ILenderState>),
        isFetchingLendersList: false,
      }),
    );
    builder.addCase(getLendersListThunk.pending, (state: ILendersState) => ({
      ...state,
      isFetchingLendersList: true,
    }));
    builder.addCase(getLendersListThunk.rejected, (state: ILendersState) => ({
      ...state,
      isFetchingLendersList: false,
    }));
    builder.addCase(getLenderThunk.fulfilled, (state: ILendersState, { payload }: PayloadAction<ILenderRequest>) => ({
      ...state,
      lendersHashTable: {
        ...state.lendersHashTable,
        [state.currentLenderId]: {
          ...state.lendersHashTable[state.currentLenderId],
          ...payload,
          initialValues: {
            ...state.lendersHashTable[state.currentLenderId].initialValues,
            preferences: {
              ...state.lendersHashTable[state.currentLenderId].initialValues.preferences,
              ltvTaxesFeesFilter:
                // TODO: need to change ltvTaxesFeesFilter type to boolean in all project
                state.lendersHashTable[state.currentLenderId].ltv_calculation_method === ''
                  ? payload.ltv_calculation_method === LTV_INC_TAXES_FEES
                    ? 'true'
                    : 'false'
                  : state.lendersHashTable[state.currentLenderId].initialValues.preferences.ltvTaxesFeesFilter,
            },
          },
        },
      },
      isFetchingLender: false,
      isLoadedLender: true,
    }));
    builder.addCase(getLenderThunk.pending, (state: ILendersState) => ({
      ...state,
      isFetchingLender: true,
      isLoadedLender: false,
    }));
    builder.addCase(getLenderThunk.rejected, (state: ILendersState) => ({
      ...state,
      isFetchingLender: false,
      isLoadedLender: true,
    }));
    builder.addCase(
      getVehiclesForLenderThunk.fulfilled,
      (state: ILendersState, { payload, meta: { arg: request_args } }) => {
        const lenderId = getHashTableIdByLenderName(state.lendersHashTable, request_args.lender_name as string);
        const results = payload.results || [];
        const availableFilters = payload.available_filters;

        if (lenderId) {
          return {
            ...state,
            lendersHashTable: {
              ...state.lendersHashTable,
              [lenderId]: {
                ...state.lendersHashTable[lenderId],
                vehicles: {
                  ...state.lendersHashTable[lenderId].vehicles,
                  vehiclesByParams: results,
                },
                availableFilters,
                isFetchingVehicles: false,
                isFetchingVehiclesCount: false,
                isFetchingVehiclesFailed: false,
              },
            },
          };
        }
      },
    );
    builder.addCase(getVehiclesForLenderThunk.pending, (state: ILendersState, { meta: { arg: request_args } }) => {
      const lenderId = getHashTableIdByLenderName(state.lendersHashTable, request_args.lender_name as string);

      if (lenderId) {
        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [lenderId]: {
              ...state.lendersHashTable[lenderId],
              vehicles: { ...state.lendersHashTable[lenderId].vehicles, vehiclesByParams: [], sortedVehicles: [] },
              availableFilters: state.lendersHashTable[lenderId].availableFilters,
              resetCount: 0,
              isFetchingVehicles: true,
              isFetchingVehiclesCount: true,
              isFetchingVehiclesFailed: false,
              fetchVehiclesError: undefined,
            },
          },
        };
      }
    });
    builder.addCase(getVehiclesForLenderThunk.rejected, (state: ILendersState, action) => {
      const lenderId = getHashTableIdByLenderName(state.lendersHashTable, action.meta.arg.lender_name as string);

      if (lenderId) {
        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [lenderId]: {
              ...state.lendersHashTable[lenderId],
              vehicles: { ...state.lendersHashTable[lenderId].vehicles, vehiclesByParams: [] },
              availableFilters: state.lendersHashTable[lenderId].availableFilters,
              isFetchingVehicles: false,
              isFetchingVehiclesCount: false,
              isFetchingVehiclesFailed: true,
              fetchVehiclesError: action.payload,
            },
          },
        };
      }
    });

    builder.addCase(
      getLenderVehiclesByStockThunk.fulfilled,
      (state: ILendersState, { payload, meta: { arg: request_args } }) => {
        const lenderId = getHashTableIdByLenderName(state.lendersHashTable, request_args.lender_name as string);

        if (lenderId) {
          return {
            ...state,
            lendersHashTable: {
              ...state.lendersHashTable,
              [lenderId]: {
                ...state.lendersHashTable[lenderId],
                vehicles: {
                  ...state.lendersHashTable[lenderId].vehicles,
                  vehiclesByStock: payload.vehicles,
                },
                availableFilters:
                  state.lendersHashTable[lenderId].vehicles.vehiclesByParams !== undefined
                    ? state.lendersHashTable[lenderId].availableFilters
                    : undefined,
                notFoundVehicles: payload.search_result.not_in_db,
                notAvailableVehicles: payload.search_result.not_available,
                isFetchingVehicles: false,
                isFetchingVehiclesCount: false,
                isFetchingVehiclesFailed: false,
              },
            },
          };
        }
      },
    );

    builder.addCase(getLenderVehiclesByStockThunk.pending, (state: ILendersState, { meta: { arg: request_args } }) => {
      const lenderId = getHashTableIdByLenderName(state.lendersHashTable, request_args.lender_name as string);

      if (lenderId) {
        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [lenderId]: {
              ...state.lendersHashTable[lenderId],
              vehicles: { ...state.lendersHashTable[lenderId].vehicles, vehiclesByStock: [], sortedVehicles: [] },
              availableFilters:
                state.lendersHashTable[lenderId].vehicles.vehiclesByParams !== undefined
                  ? state.lendersHashTable[lenderId].availableFilters
                  : undefined,
              resetCount: 0,
              isFetchingVehicles: true,
              isFetchingVehiclesCount: true,
              isFetchingVehiclesFailed: false,
              fetchVehiclesError: undefined,
            },
          },
        };
      }
    });

    builder.addCase(getLenderVehiclesByStockThunk.rejected, (state: ILendersState, action) => {
      const lenderId = getHashTableIdByLenderName(state.lendersHashTable, action.meta.arg.lender_name as string);

      if (lenderId) {
        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [lenderId]: {
              ...state.lendersHashTable[lenderId],
              vehicles: { ...state.lendersHashTable[lenderId].vehicles, vehiclesByStock: [] },
              availableFilters:
                state.lendersHashTable[lenderId].vehicles.vehiclesByParams !== undefined
                  ? state.lendersHashTable[lenderId].availableFilters
                  : undefined,
              isFetchingVehicles: false,
              isFetchingVehiclesCount: false,
              isFetchingVehiclesFailed: true,
              fetchVehiclesError: action.payload,
            },
          },
        };
      }
    });
    builder.addCase(
      getLenderControlsThunk.fulfilled,
      (state: ILendersState, { payload }: PayloadAction<ILenderControlsResponse>) => ({
        ...state,
        currentLenderControls: {
          maxVSC: payload.max_vsc_amount,
          maxGap: payload.max_gap_amount,
          showRates: payload.show_rates === 'yes',
          showAdvancedOverrides: payload.show_advanced_overrides,
          isVscTaxable: payload.is_vsc_taxable ?? true,
          isGapTaxable: payload.is_gap_taxable ?? true,
        },
        isFetchingLender: false,
      }),
    );
    builder.addCase(getLenderControlsThunk.pending, (state: ILendersState) => ({
      ...state,
      isFetchingLender: true,
    }));
    builder.addCase(getLenderControlsThunk.rejected, (state: ILendersState) => ({
      ...state,
      isFetchingLender: false,
    }));

    builder.addCase(
      fetchDealSourceDataThunk.fulfilled,
      (state: ILendersState, { payload: dealSourceData }: PayloadAction<IDealSourceData>) => {
        if (state.currentLenderId) {
          const lender = state.lendersHashTable[state.currentLenderId];

          return {
            ...state,
            lendersHashTable: {
              ...state.lendersHashTable,
              [state.currentLenderId]: {
                ...lender,
                dealSourceData: preserveManuallyEnteredVehicleInfo(lender.dealSourceData, dealSourceData),
                dealSourceDataFlags: computeGetFulfilledFlags(lender.dealSourceDataFlags),
              },
            },
          };
        }
      },
    );
    builder.addCase(fetchDealSourceDataThunk.pending, (state: ILendersState) => {
      if (state.currentLenderId) {
        const lender = state.lendersHashTable[state.currentLenderId];

        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [state.currentLenderId]: {
              ...lender,
              dealSourceDataFlags: computeGetPendingFlags(lender.dealSourceDataFlags),
            },
          },
        };
      }
    });
    builder.addCase(fetchDealSourceDataThunk.rejected, (state: ILendersState) => {
      if (state.currentLenderId) {
        const lender = state.lendersHashTable[state.currentLenderId];

        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [state.currentLenderId]: {
              ...lender,
              dealSourceDataFlags: computeGetRejectedFlags(lender.dealSourceDataFlags),
            },
          },
        };
      }
    });
    builder.addCase(submitDealSourceDataThunk.fulfilled, (state: ILendersState) => {
      if (state.currentLenderId) {
        const lender = state.lendersHashTable[state.currentLenderId];

        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [state.currentLenderId]: {
              ...lender,
              dealSourceDataFlags: computePostFulfilledFlags(lender.dealSourceDataFlags),
            },
          },
        };
      }
    });
    builder.addCase(submitDealSourceDataThunk.pending, (state: ILendersState) => {
      if (state.currentLenderId) {
        const lender = state.lendersHashTable[state.currentLenderId];

        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [state.currentLenderId]: {
              ...lender,
              dealSourceDataFlags: computePostPendingFlags(lender.dealSourceDataFlags),
            },
          },
        };
      }
    });
    builder.addCase(submitDealSourceDataThunk.rejected, (state: ILendersState) => {
      if (state.currentLenderId) {
        const lender = state.lendersHashTable[state.currentLenderId];

        return {
          ...state,
          lendersHashTable: {
            ...state.lendersHashTable,
            [state.currentLenderId]: {
              ...lender,
              dealSourceDataFlags: computePostRejectedFlags(lender.dealSourceDataFlags),
            },
          },
        };
      }
    });
  },
});

export const {
  syncInitialValues,
  syncInitialErrors,
  setCurrentLenderId,
  resetVehicles,
  resetVehiclesByStock,
  resetVehiclesByParams,
  setIncome,
  resetIncome,
  resetByStockOptions,
  resetByStock,
  resetDealSourceData,
  resetDealSourceDataFlags,
  incrementResetCount,
  resetResetCount,
  resetVehicleFiltersDirtyFields,
  setIsTableView,
  setDealSourceTierEditable,
  setFirstDealSourceRenderOnLender,
  setBalloonInitDataLoaded,
  setSortedVehiclesData,
  setDealSourceData,
} = lenderSlice.actions;
